import { AdminVideo } from '../model/dto/video/admin-video';
import {
  FailureReasonDisplay,
  VideoFrameOrientation,
  VideoStateNotice,
  VideoStateNoticeTone,
} from '../model/type/video-state-notice.type';
import { VideoUploadItem } from '../../context/video-upload.types';
import { getVideoStatusDisplay, isArchived } from './video-status-utils';

const TIMEOUT_REASON = 'timeout';

export const getFailureReasonDisplay = (
  reason: string | null
): FailureReasonDisplay => {
  if (reason === null || !reason.trim()) {
    return { kind: 'none' };
  }

  return reason === TIMEOUT_REASON
    ? { kind: 'timeout' }
    : { kind: 'other', detail: reason };
};

export const getStateNoticeTone = (
  notice: VideoStateNotice
): VideoStateNoticeTone => {
  switch (notice.kind) {
    case 'placement':
      switch (notice.notice) {
        case 'failed':
        case 'uploadStopped':
          return 'warning';

        case 'pending':
        case 'archived':
          return 'info';

        default: {
          const unhandledNotice: never = notice.notice;

          return unhandledNotice;
        }
      }

    case 'transcodeFailed':
    case 'uploadFailed':
    case 'replacementFailed':
      return 'failure';

    case 'uploadNotFinished':
    case 'replacementUnavailable':
      return 'warning';

    case 'archivedRecord':
    case 'processing':
    case 'uploading':
    case 'replacementProcessing':
    case 'replacementUploading':
    case 'replacesVideo':
    case 'stagedAppears':
    case 'stagedBatch':
      return 'info';

    default: {
      const unhandledNotice: never = notice;

      return unhandledNotice;
    }
  }
};

export const getReplacementNotice = (
  replacement: AdminVideo,
  upload?: VideoUploadItem
): VideoStateNotice => {
  const display = getVideoStatusDisplay(replacement, upload);

  switch (display.kind) {
    case 'queued':
      return { kind: 'replacementUploading', percent: 0 };

    case 'uploading':
      return { kind: 'replacementUploading', percent: display.percent };

    case 'finishing':
      return { kind: 'replacementUploading', percent: 100 };

    case 'uploadFailed':
    case 'uploadNotFinished':
      return { kind: 'replacementFailed' };

    case 'status':
      return display.status === 'failed'
        ? { kind: 'replacementFailed' }
        : { kind: 'replacementProcessing' };

    default: {
      const unhandledDisplay: never = display;

      return unhandledDisplay;
    }
  }
};

export const getVideoOrientation = (
  video: AdminVideo
): VideoFrameOrientation => {
  const width = video.width ?? video.source_width;
  const height = video.height ?? video.source_height;

  if (width === height) {
    return 'square';
  }

  return height > width ? 'portrait' : 'landscape';
};

const getStatusNotice = (
  video: AdminVideo,
  upload: VideoUploadItem | undefined,
  canDelete: boolean
): VideoStateNotice | null => {
  const display = getVideoStatusDisplay(video, upload);

  switch (display.kind) {
    case 'queued':
    case 'uploading':
    case 'finishing':
      return { kind: 'uploading' };

    case 'uploadFailed':
      return { kind: 'uploadFailed' };

    case 'uploadNotFinished':
      return { kind: 'uploadNotFinished', canDelete };

    case 'status':
      switch (display.status) {
        case 'processing':
          return { kind: 'processing' };

        case 'failed':
          return { kind: 'transcodeFailed', reason: video.failure_reason };

        case 'ready':
        case 'uploading':
          return null;

        default: {
          const unhandledStatus: never = display.status;

          return unhandledStatus;
        }
      }

    default: {
      const unhandledDisplay: never = display;

      return unhandledDisplay;
    }
  }
};

export const getVideoLifecycleNotices = (
  video: AdminVideo,
  upload: VideoUploadItem | undefined,
  canDelete: boolean
): VideoStateNotice[] => {
  const archived = isArchived(video);
  const statusNotice = getStatusNotice(video, upload, canDelete && !archived);

  return [
    ...(archived ? [{ kind: 'archivedRecord' } as const] : []),
    ...(statusNotice ? [statusNotice] : []),
  ];
};
